/**
 * SIM Status Card
 * Visar och hanterar SIM-status via backend API
 * Backend använder centraliserad JasperService för alla Jasper API-anrop
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import {
  SignalIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface SimStatusData {
  iccid: string;
  imei: string;
  status: string;
  msisdn?: string;
  ratePlan?: string;
  communicationPlan?: string;
  dateActivated?: string;
  ctdDataUsage?: number;
  ctdSMSUsage?: number;
  ctdSessionCount?: number;
  overageLimitReached?: boolean;
  ipAddress?: string;
  apn?: string;
}

interface SimStatusCardProps {
  imei: string;
  iccid?: string;
  language: string;
  onStatusChange?: () => void;
}

// SIM-status konfiguration
const simStatusConfig: Record<string, { labelKey: string; color: string; bgColor: string }> = {
  ACTIVATED: { labelKey: 'copy.ui.simActivated', color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  ACTIVATION_READY: { labelKey: 'copy.ui.simActivationReady', color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  DEACTIVATED: { labelKey: 'copy.ui.simDeactivated', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  INVENTORY: { labelKey: 'copy.ui.simInventory', color: 'text-textSecondary', bgColor: 'bg-bgBase' },
  RETIRED: { labelKey: 'copy.ui.simRetired', color: 'text-textSecondary', bgColor: 'bg-bgBase' },
  TEST_READY: { labelKey: 'copy.ui.simTestReady', color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
  PURGED: { labelKey: 'copy.ui.simPurged', color: 'text-textTertiary', bgColor: 'bg-bgBase' },
};

const validStatuses = ['ACTIVATED', 'ACTIVATION_READY', 'DEACTIVATED', 'INVENTORY', 'RETIRED', 'TEST_READY'];

const SimStatusCard: React.FC<SimStatusCardProps> = ({ imei, iccid, onStatusChange }) => {
  const { t } = useTranslation();
  const [simData, setSimData] = useState<SimStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const fetchSimStatus = useCallback(async () => {
    console.log('[SimStatusCard] fetchSimStatus called, iccid:', iccid, 'imei:', imei);
    if (!iccid) {
      console.log('[SimStatusCard] No ICCID, returning');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      console.log('[SimStatusCard] Calling API:', `/api/devices/${imei}/sim-status`);
      const response = await api.get(`/api/devices/${imei}/sim-status`);
      console.log('[SimStatusCard] API response:', response.data);
      if (response.data.success) {
        setSimData(response.data.data);
      } else {
        console.log('[SimStatusCard] API returned success:false', response.data.error);
        setError(response.data.error || t('copy.ui.apiError'));
      }
    } catch (err: any) {
      console.error('[SimStatusCard] API error:', err);
      const errorMsg = err?.response?.data?.error || err?.message || t('copy.ui.simStatusFetchError');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [imei, iccid, t]);

  useEffect(() => {
    if (iccid) {
      fetchSimStatus();
    }
  }, [fetchSimStatus, iccid]);

  const handleStatusChange = async (newStatus: string) => {
    try {
      setChanging(true);
      setShowStatusDropdown(false);
      
      const response = await api.put(`/api/devices/${imei}/sim-status`, { status: newStatus });
      
      if (response.data.success) {
        // Uppdatera lokal data
        setSimData(prev => prev ? { ...prev, status: newStatus } : null);
        onStatusChange?.();
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || t('copy.ui.simStatusChangeError');
      setError(errorMsg);
    } finally {
      setChanging(false);
    }
  };

  const formatBytes = (bytes?: number): string => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  if (!iccid) {
    return (
      <div className="bg-bgElevated rounded-lg border border-borderDefault p-4">
        <div className="flex items-center gap-2 text-textSecondary">
          <SignalIcon className="w-5 h-5" />
          <span>{t('copy.ui.simNoIccid')}</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-bgElevated rounded-lg border border-borderDefault p-4">
        <div className="flex items-center justify-center py-4">
          <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-textSecondary">
            {t('copy.ui.simFetching')}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-bgElevated rounded-lg border border-borderDefault p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-500">
            <ExclamationTriangleIcon className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button
            onClick={fetchSimStatus}
            className="p-2 hover:bg-bgHover rounded-lg transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = simData?.status || 'UNKNOWN';
  const statusInfo = simStatusConfig[currentStatus] || {
    labelKey: '',
    color: 'text-textSecondary',
    bgColor: 'bg-gray-100',
  };
  const statusText = (info: { labelKey: string }) => (info.labelKey ? t(info.labelKey) : currentStatus);

  return (
    <div className="bg-bgElevated rounded-lg border border-borderDefault p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-textDefault flex items-center gap-2">
          <SignalIcon className="w-5 h-5" />
          {t('copy.ui.simCardJasper')}
        </h3>
        <button
          onClick={fetchSimStatus}
          disabled={loading}
          className="p-2 hover:bg-bgHover rounded-lg transition-colors"
          title={t('common.refresh')}
        >
          <ArrowPathIcon className={`w-5 h-5 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {simData && (
        <div className="space-y-3">
          {/* Status med dropdown för ändring */}
          <div className="flex justify-between items-center">
            <span className="text-textSecondary">{t('common.status')}</span>
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={changing}
                className={`flex items-center gap-2 px-3 py-1 rounded-full ${statusInfo.bgColor} ${statusInfo.color} hover:opacity-80 transition-opacity`}
              >
                {changing ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : currentStatus === 'ACTIVATED' ? (
                  <CheckCircleIcon className="w-4 h-4" />
                ) : currentStatus === 'DEACTIVATED' ? (
                  <XCircleIcon className="w-4 h-4" />
                ) : null}
                <span className="text-sm font-medium">
                  {statusText(statusInfo)}
                </span>
                <ChevronDownIcon className="w-4 h-4" />
              </button>

              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-bgElevated rounded-lg shadow-lg border border-borderDefault z-10">
                  {validStatuses.map((status) => {
                    const info = simStatusConfig[status];
                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        disabled={status === currentStatus}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-bgHover transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          status === currentStatus ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <span className={info.color}>
                          {statusText(info)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* MSISDN */}
          {simData.msisdn && (
            <div className="flex justify-between">
              <span className="text-textSecondary">MSISDN</span>
              <span className="font-mono text-textDefault">{simData.msisdn}</span>
            </div>
          )}

          {/* Rate Plan */}
          {simData.ratePlan && (
            <div className="flex justify-between">
              <span className="text-textSecondary">{t('copy.ui.simRatePlan')}</span>
              <span className="text-textDefault text-sm">{simData.ratePlan}</span>
            </div>
          )}

          {/* Communication Plan */}
          {simData.communicationPlan && (
            <div className="flex justify-between">
              <span className="text-textSecondary">{t('copy.ui.simCommPlan')}</span>
              <span className="text-textDefault text-sm">{simData.communicationPlan}</span>
            </div>
          )}

          {/* Användning */}
          {(simData.ctdDataUsage !== undefined || simData.ctdSMSUsage !== undefined) && (
            <>
              <div className="border-t border-borderDefault my-3" />
              <div className="text-sm text-textSecondary mb-2">
                {t('copy.ui.simUsagePeriod')}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-bgDefault rounded-lg p-2 text-center">
                  <div className="text-lg font-semibold text-textDefault">
                    {formatBytes(simData.ctdDataUsage)}
                  </div>
                  <div className="text-xs text-textSecondary">Data</div>
                </div>
                <div className="bg-bgDefault rounded-lg p-2 text-center">
                  <div className="text-lg font-semibold text-textDefault">
                    {simData.ctdSMSUsage || 0}
                  </div>
                  <div className="text-xs text-textSecondary">SMS</div>
                </div>
                <div className="bg-bgDefault rounded-lg p-2 text-center">
                  <div className="text-lg font-semibold text-textDefault">
                    {simData.ctdSessionCount || 0}
                  </div>
                  <div className="text-xs text-textSecondary">{t('copy.ui.simSessions')}</div>
                </div>
              </div>
            </>
          )}

          {/* Session info */}
          {simData.ipAddress && (
            <>
              <div className="border-t border-borderDefault my-3" />
              <div className="flex justify-between">
                <span className="text-textSecondary">IP</span>
                <span className="font-mono text-textDefault text-sm">{simData.ipAddress}</span>
              </div>
            </>
          )}

          {simData.apn && (
            <div className="flex justify-between">
              <span className="text-textSecondary">APN</span>
              <span className="text-textDefault text-sm">{simData.apn}</span>
            </div>
          )}

          {/* Varning vid overage */}
          {simData.overageLimitReached && (
            <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3 flex items-center gap-2 text-orange-600">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="text-sm">
                {t('copy.ui.simDataLimit')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimStatusCard;

